import { Component, OnInit } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { HeaderComponent } from "../../core/components/header/header.component";
import { cpfValido } from '../../core/validators/cpf-validator';
import { NgxMaskDirective } from 'ngx-mask';
import { Router, ActivatedRoute } from '@angular/router';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { lastValueFrom } from 'rxjs';
import { LocalStorageService } from '../../core/services/local-storage.service';
import { HttpClientService } from '../../core/services/http-client.service';
import { DadosContato } from '../../core/models/dados-contato';
import { EnumStatusResponse } from 'src/app/core/enum/EnumHttpResponse';
import { LoaderService } from 'src/app/core/services/loader.service';
import { ExtractService } from 'src/app/core/services/extract.service';

@Component({
  selector: 'app-autenticacao',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule, HeaderComponent, NgxMaskDirective],
  templateUrl: './autenticacao.component.html',
  styleUrl: './autenticacao.component.scss'
})
export class AutenticacaoComponent implements OnInit {
  mostrarHeader: boolean = false;
  erroAutenticacao: boolean = false;
  mensagemErro: string = '';

  private decoded: any | null = null;
  tokenCrip?: string;

  form: FormGroup;
  isAreaAberta: boolean = false; // Flag para identificar se veio da área aberta
  isLoading: boolean = false; // Flag para controlar estado de loading

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private http: HttpClient,
    private localStorageService: LocalStorageService,
    private httpClientService: HttpClientService,
    private loaderService: LoaderService,
    private extractService: ExtractService
  ) {
    // Formulário de validação do usuário - CPF e Data de Nascimento
    this.form = this.fb.group({
      cpf: ['', [Validators.required, cpfValido]],
      dataNascimento: ['', Validators.required]
    });
  }

  // Decodifica o token do localStorage (sToken), usado para decidir o fluxo de area aberta ou fechada
  private decodeFromSToken(): any | null {
    const sToken = this.localStorageService.get('sToken');
    if (!sToken) return null;
    try {
      return this.extractService.getDecodedAccessToken(sToken);
    } catch {
      return null;
    }
  }

  ngOnInit(): void {
    // Lê os parâmetros da query string - controla header e identifica se veio com token
    this.route.queryParams.subscribe(params => {
      const mostrarHeaderParam = params['MostrarHeader'];
      const hasTokenParam = params['hasToken'];

      this.mostrarHeader = mostrarHeaderParam === 'true';

      // salva MostrarHeader como fallback global
      if (typeof window !== 'undefined') {
        this.localStorageService.add('MostrarHeader', this.mostrarHeader ? 'true' : 'false');
      }

      // Quando hasToken=true, a tela espera que o sToken já esteja no localStorage
      if (hasTokenParam === 'true') {
        const sToken = this.localStorageService.get('sToken');

        if (sToken) {
          this.tokenCrip = sToken;
          this.decoded = this.decodeFromSToken();
          this.isAreaAberta = false; // com token => fluxo restrito
        }
      }

      // MostrarHeader=true e sem token => área aberta
      if (this.mostrarHeader && !this.decoded) {
        this.isAreaAberta = true;
      }

      // Sem header e sem token => acesso inválido, redireciona para not-found
      if (!this.mostrarHeader && !this.decoded) {
        this.router.navigate(['/not-found']);
      }
    });
  }

  // Completa CPF com zeros à esquerda
  onCpfBlur(): void {
    const control = this.form.get('cpf');
    if (!control?.value) return;

    const cpfComZeros = control.value.padStart(11, '0');

    control.setValue(cpfComZeros);
    control.markAsTouched();
  }

  /**
   * Submit do formulário:
   * - Área aberta: busca dados de contato + login de sessão + navega para AuthenticationOpen
   * - Área fechada: valida token de acesso e navega para home
   */
  onSubmit(): void {
    if (this.form.invalid || this.isLoading) return;

    this.isLoading = true; // Inicia loading

    // Remove formatação do CPF
    const cpf = this.form.get('cpf')?.value?.replace(/\D/g, '');
    const dataNascimento = this.form.get('dataNascimento')?.value;

    if (this.isAreaAberta) {
      this.handleAreaAberta(cpf, dataNascimento);
    } else {
      this.handleAreaFechada(cpf, dataNascimento);
    }
  }

  /**
   * Fluxo de área aberta:
   * 1) consulta dados de contato (nome, telefone, email)
   * 2) salva dados no localStorage
   * 3) realiza login de sessão (gera bearer/refresh)
   * 4) navega para AuthenticationOpen
   */
  private async handleAreaAberta(cpf: string, dataNascimento: string): Promise<void> {
    try {
      this.loaderService.showLoader();
      const response = await this.httpClientService.getDadosContato(cpf, dataNascimento);

      if (response.status === 200 && response.body) {
        const dadosContato: DadosContato = response.body;

        this.localStorageService.add('cpf', cpf);
        this.localStorageService.add('nome', dadosContato.nome);

        // Salva arrays de telefones e emails como JSON
        this.localStorageService.add('telefones', JSON.stringify(dadosContato.telefones));
        this.localStorageService.add('emails', JSON.stringify(dadosContato.emails));

        this.localStorageService.add('MostrarHeader', this.mostrarHeader ? 'true' : 'false');

        await this.sessionLoginHandler(Number(cpf));

        this.router.navigate(['/AuthenticationOpen'], {
          queryParams: { MostrarHeader: this.mostrarHeader ? 'true' : 'false' }
        });

        this.loaderService.hideLoader();
        return;
      }

      // Falha de validação/retorno (ex: dados não encontrados)
      this.isLoading = false;
      this.mensagemErro = 'Erro ao buscar dados de contato. Verifique o CPF e a data de nascimento.';
      this.erroAutenticacao = true;
    } catch (error) {
      console.error('Erro ao buscar dados de contato:', error);
      this.isLoading = false;
      this.erroAutenticacao = true;
      this.mensagemErro = 'Erro ao buscar dados de contato. Verifique o CPF e a data de nascimento.';
      this.erroAutenticacao = true;
      this.loaderService.hideLoader();
    }
  }

  /**
   * Fluxo de área fechada:
   * Salva MostrarHeader e chama validação com token (sToken) para autenticar e liberar acesso.
   */
  private handleAreaFechada(cpf: string, dataNascimento: string): void {
    this.localStorageService.add('MostrarHeader', this.mostrarHeader ? 'true' : 'false');
    this.getValidaAutenticacao(Number(cpf), dataNascimento, this.tokenCrip || '');
  }

  private async sessionLogin(cpf: number): Promise<HttpResponse<any>> {
    const url = `${environment.baseUrl}/apis/api-adesao-cadastro/v1/Sessao/login`;
    const headers = new HttpHeaders({
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": `${environment.ocpDadosCadastrais}`
    });

    return await lastValueFrom(this.http.post<any>(url, { cpf: cpf }, { headers, observe: 'response' }))
      .catch(error => {
        return new HttpResponse({ body: error });

      });
  }

  private async sessionLoginHandler(cpf: number): Promise<void> {
    try {
      const response: HttpResponse<any> = await this.sessionLogin(cpf);

      if (response.status === 200) {
        this.localStorageService.add("bearerToken", response.body.bearerToken);
        this.localStorageService.add("refreshToken", response.body.refreshToken);
      } else {
        throw new Error(`Login falhou com status: ${response.status}`);
      }
    } catch (error) {
      console.error("Erro ao realizar login de sessão:", error);
      throw error;
    }
  }

  private getValidaAutenticacao(cpf: number, dataNascimento: string, token: string): any {
    this.loaderService.showLoader();
    this.httpClientService.getValidaAutenticacao(cpf, dataNascimento, token)
      .then((response: HttpResponse<any>) => {

        if (response.status === EnumStatusResponse.BadRequest) {
          this.isLoading = false;
          this.mensagemErro = 'Os dados informados estão incorretos, por favor verifique as informações e tente novamente.';
          this.erroAutenticacao = true;
          return;
        }

        if (response.status !== EnumStatusResponse.Success) return;

        if (response.status === 200){
          this.router.navigate(['/home']);
        }
      })
      .catch(error => {
        this.mensagemErro = 'Os dados informados estão incorretos, por favor verifique as informações e tente novamente.';
        this.erroAutenticacao = true;
        console.error('Erro ao validar token de acesso:', error);
      })
      .finally(() => {
        this.isLoading = false;
        this.loaderService.hideLoader();
      });
  }
}
